// Full screen: the board fills the screen with nothing else on it, fitted so
// every widget shows (or just the selected ones). It works as the board does:
// widgets take clicks, keys and the wheel, and what you open (a widget's
// settings, its menu, a panel, the chat) shows. Esc closes what is open, then
// leaves, and the view comes back exactly as it was. Where the browser will
// not go full screen, the controls still hide and Esc still leaves.
//
// A doc, sheet, deck or files board fills the tab instead (Features 7), under one bar: Back, the board's name, where
// you are in it, the file's own tools, then hiding the browser too. The browser's own full screen shows nothing of
// ours, and its Esc comes back to the bar. A board that fills the tab is remembered as one, board by board.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";
import { kindOf } from "../../shared/boardKinds";
import { Maximize2 } from "lucide-react";
import type { Board, BoardKind } from "../../shared/types";
import { camera, getInsets, setInsets, useCamera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import { withMap } from "../boards/mindmap/live";
import { boardItems, currentBoard, get, set, useStore } from "../state/store";
import { chat, closeChat, openChat, useChat } from "../agent/store";
import { cx, isTyping } from "../lib/util";
import { readStoredJson, writeStored } from "../lib/stored";
import { FullscreenBar } from "./FullscreenBar";
import { IconButton } from "./ui";

let before: { board: string | null; cam: { x: number; y: number; z: number }; insets: ReturnType<typeof getInsets>; selection: string[]; chat: (reopen: boolean) => void } | null = null;

const EDGE = { left: 24, right: 24, top: 24, bottom: 24 };

/** The boards worked on, not shown: their full screen fills the tab, the browser's tabs and address bar still there,
 *  with a Back button, as a Google Sheets tab (Fixes 10). The browser's own full screen is the second choice. */
export const TAB_KINDS = new Set<BoardKind>(["doc", "sheet", "slides", "files"]);

/** Whether the board in view fills the tab: a doc, sheet, deck or files board in full screen. */
export function fillsTab(s: ReturnType<typeof get>): boolean {
  return s.presenting && TAB_KINDS.has(kindOf(currentBoard(s)));
}

/** What can be open in front of the work: a dialog, a menu, a popover, the find bar, the slide sorter or master. */
const LAYERS = ".dialog-scrim, [role=dialog], [role=menu], .menu, .board-menu, .sh-find, .sl-sorter, .sl-mst-surface, .sl-pop, .st-pop, .cmdbar-wrap.is-open, .ph";

/** Whether something is open that an Esc is meant for. Read when the key goes down, before anything closes. */
export function layerOpen(): boolean {
  return [...document.querySelectorAll<HTMLElement>(LAYERS)].some((el) => el.getClientRects().length > 0);
}

/** What full screen shows now: the browser's own full screen too, and the room the bar and the widget shelf take. */
export const useFs = create<{ browser: boolean; shelf: boolean; where: HTMLElement | null; tools: HTMLElement | null }>(() => ({ browser: false, shelf: false, where: null, tools: null }));

/** A board's own parts in the bar: where you are in it (`where`) and the file's tools (`tools`). Nothing outside full screen's bar. */
export function InFsBar({ part, children }: { part: "where" | "tools"; children: React.ReactNode }) {
  const el = useFs((s) => s[part]);
  return el ? createPortal(children, el) : null;
}

/** Each board's way of being left, on its own (Features 7): a board that filled the tab opens that way when it is
 *  come back to, and one left with Back opens as the board view. Kept in this browser. */
const FS_KEY = "cb:fs";
const remembered = (id: string): boolean => !!readStoredJson<Record<string, boolean>>(FS_KEY, {})[id];
function remember(id: string, on: boolean) {
  const all = readStoredJson<Record<string, boolean>>(FS_KEY, {});
  if (!!all[id] === on) return;
  if (on) all[id] = true;
  else delete all[id];
  writeStored(FS_KEY, JSON.stringify(all));
}

/**
 * Puts an open chat aside while the board has the screen to itself. What it gives back brings the chat
 * back, unless someone opened or closed it in the meantime: then theirs is the last word.
 */
function chatAside(): (reopen: boolean) => void {
  if (!chat.get().open) return () => {};
  closeChat();
  let touched = false;
  const stop = useChat.subscribe((s, p) => {
    if (s.open !== p.open) touched = true;
  });
  return (reopen) => {
    stop();
    if (reopen && !touched) openChat();
  };
}

/** Fits the screen to what is presented: the whole board, or the widgets shown alone. */
export function fitForScreen() {
  const s = get();
  const picked = (s.presentIds ?? []).map((id) => s.items[id]).filter(Boolean);
  const rect = bounds(picked.length ? picked.map(itemRect) : withMap(boardItems(s).map(itemRect)));
  // a widget on its own fills the screen; a whole board stops a little over life size
  camera.fit(rect, { maxZ: picked.length ? 4 : 1.6, pad: picked.length ? 56 : 40 });
}

/**
 * The whole board, full screen. With solo, only the selected widgets: asked
 * for on purpose (the widget's menu, the command bar), never because
 * something happened to be selected.
 */
export async function enterFullscreen(opts: { solo?: boolean; browser?: boolean } = {}) {
  if (get().presenting) return;
  const c = useCamera.getState();
  const picked = get().selection;
  const sel = opts.solo ? picked.filter((id) => get().items[id]?.kind === "widget") : [];
  // a dialog holding words someone typed and has not saved keeps its place, and the window stays as it is
  if (!set.made({ presenting: true, focusMode: true, panel: null, panelArg: null, presentIds: sel.length ? sel : null, selection: [] })) return;
  // full screen opens on the board alone; the chat comes back with Ctrl or ⌘ J, and on the way out
  before = { board: null, cam: { x: c.x, y: c.y, z: c.z }, insets: getInsets(), selection: picked, chat: chatAside() };
  setInsets(EDGE);
  const board = currentBoard(get());
  before.board = board?.id ?? null;
  if (board && TAB_KINDS.has(kindOf(board))) remember(board.id, true);
  useFs.setState({ shelf: false });
  if (opts.browser ?? !TAB_KINDS.has(kindOf(board))) await browserFullscreen();
  // the window is a new size once the browser is full screen, and the chat has made its room over to the board
  setTimeout(() => {
    if (!get().presenting) return;
    if (!chat.get().open) setInsets(EDGE);
    fitForScreen();
  }, 180);
}

/** The browser's own full screen, its tabs and address bar hidden too. */
export async function browserFullscreen() {
  try {
    if (document.fullscreenEnabled && !document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: "hide" });
  } catch {
    /* the browser said no: the controls are hidden anyway, and Esc still leaves */
  }
}

/** Leaves full screen. keep: the board stays remembered as one that fills the tab (a move to another board, not a Back). */
export function exitFullscreen(opts: { keep?: boolean } = {}) {
  const s = get();
  if (!s.presenting) return;
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
  if (!opts.keep && s.boardId) remember(s.boardId, false);
  const back = before;
  set({ presenting: false, focusMode: false, presentIds: null, selection: (back?.selection ?? []).filter((id) => s.items[id]?.boardId === s.boardId) });
  if (back) {
    setInsets(back.insets);
    const cam = back.cam;
    back.chat(true);
    before = null;
    // after the window has its old size again, and only for the board it was taken on
    setTimeout(() => get().boardId === back.board && useCamera.setState({ ...cam, animating: false }), 120);
  }
}

export function toggleFullscreen() {
  if (get().presenting) exitFullscreen();
  else void enterFullscreen();
}

/**
 * Full screen for a board looked at, not worked on (a public link, a board shared with you to view): the button in
 * its bar, and F and Esc where the page has no shortcuts of its own. A viewed deck has its own, which plays it.
 */
export function ViewFullscreenButton({ board, edit = false }: { board: Board; edit?: boolean }) {
  if (kindOf(board) === "slides" && !edit) return null;
  return (
    <IconButton label="Full screen" kbd="F" onClick={() => void enterFullscreen()}>
      <Maximize2 size={15} />
    </IconButton>
  );
}

export function useViewFullscreenKeys(on = true) {
  useEffect(() => {
    if (!on) return;
    let layer = false;
    const down = (e: KeyboardEvent) => {
      if (e.key === "Escape") layer = get().presenting && layerOpen();
    };
    const key = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey || isTyping(e) || get().mobile) return;
      if (e.key === "Escape" && get().presenting && !layer) exitFullscreen();
      else if (e.key.toLowerCase() === "f" && !e.repeat && kindOf(currentBoard(get())) !== "slides") toggleFullscreen();
    };
    window.addEventListener("keydown", down, true);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("keydown", down, true);
      window.removeEventListener("keydown", key);
    };
  }, [on]);
}

/**
 * The parts of full screen: the bar of a board that fills the tab, and, where the browser would not go full screen,
 * one short hint, since nothing else then says how to leave. While the browser hides its tabs, nothing of ours is on
 * screen: its own indicator says how to leave.
 */
export function FullscreenLayer() {
  const presenting = useStore((s) => s.presenting);
  const tab = useStore(fillsTab);
  const browser = useFs((s) => s.browser);
  const [hint, setHint] = useState(false);
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onChange = () => {
      const on = !!document.fullscreenElement;
      useFs.setState({ browser: on });
      if (on || !get().presenting) return;
      // Esc, or the browser's own exit: a board in the tab comes back to its bar; any other leaves properly
      if (!fillsTab(get())) exitFullscreen();
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // each board remembers how it was left: one that filled the tab fills it again, and a move from one board to
  // another keeps or ends full screen by what the board moved to remembers
  const boardId = useStore((s) => currentBoard(s)?.id);
  const ready = useStore((s) => s.phase === "ready" || s.phase === "shared");
  useEffect(() => {
    const s = get();
    const board = currentBoard(s);
    if (!ready || !board || s.mobile) return;
    const again = TAB_KINDS.has(kindOf(board)) && remembered(board.id);
    if (!s.presenting) {
      if (again) void enterFullscreen({ browser: false });
    } else if (before && before.board !== board.id) {
      if (again) {
        before.board = board.id;
        before.selection = [];
      } else exitFullscreen({ keep: true });
    }
  }, [boardId, ready]);

  useEffect(() => {
    if (!presenting) {
      setHint(false);
      useFs.setState({ browser: false });
      document.querySelector(".board-root")?.classList.remove("is-still");
      return;
    }
    // the browser takes a moment to go full screen, so whether it did is read after it
    const read = setTimeout(() => useFs.setState({ browser: !!document.fullscreenElement }), 250);
    const show = setTimeout(() => !document.fullscreenElement && setHint(true), 300);
    const gone = setTimeout(() => setHint(false), 2900);
    // a board drawn on the screen hides its pointer when still; one worked in keeps it
    const root = document.querySelector(".board-root");
    const wake = () => {
      root?.classList.remove("is-still");
      if (idle.current) clearTimeout(idle.current);
      idle.current = setTimeout(() => root?.classList.add("is-still"), 2200);
    };
    if (!fillsTab(get())) window.addEventListener("pointermove", wake);
    // a resized window keeps everything in view
    const onResize = () => setTimeout(fitForScreen, 60);
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(read);
      clearTimeout(show);
      clearTimeout(gone);
      if (idle.current) clearTimeout(idle.current);
      window.removeEventListener("pointermove", wake);
      window.removeEventListener("resize", onResize);
    };
  }, [presenting]);

  if (!presenting) return <HiddenButtons />;
  // the browser's own full screen: nothing of ours over the work
  if (browser) return null;
  if (tab) return <FullscreenBar />;
  return (
    <div className={cx("fs-hint island", hint && "is-on")} role="status">
      Full screen. Press <kbd className="kbd">Esc</kbd> to leave.
    </div>
  );
}

/**
 * With the buttons hidden (Shift F), a small pill says how to get them back:
 * for a few seconds at first, and again whenever the pointer moves. The chat
 * steps aside with the buttons and comes back with them; opened meanwhile
 * (Ctrl or ⌘ J), it shows, so nothing typed goes somewhere out of sight.
 */
function HiddenButtons() {
  const hidden = useStore((s) => s.focusMode && !s.presenting);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!hidden) {
      setOn(false);
      return;
    }
    const back = chatAside();
    setOn(true);
    let t = setTimeout(() => setOn(false), 3000);
    const wake = () => {
      setOn(true);
      clearTimeout(t);
      t = setTimeout(() => setOn(false), 2200);
    };
    window.addEventListener("pointermove", wake);
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointermove", wake);
      // full screen from here keeps its own say over the chat
      back(!get().presenting);
    };
  }, [hidden]);
  if (!hidden) return null;
  return (
    <button className={cx("fs-hint island hidden-hint", on && "is-on")} onClick={() => set({ focusMode: false })}>
      Buttons hidden. Show them <kbd className="kbd">⇧F</kbd>
    </button>
  );
}
